/**
 * AdminBilling — tenant-facing `/admin/billing` page.
 *
 * Shows the tenant their current plan, what features they get, when their
 * subscription expires, and lets them pay invoices and top-up the platform
 * wallet (used to fund SMS/WhatsApp OTPs).
 */
import { useEffect, useState } from "react";
import { Download, Wallet, Check, X, Loader2, ArrowUpRight, Sparkles, MessageCircle } from "lucide-react";
import { api, inr2 } from "../../lib/api";
import { toast } from "sonner";
import { Field, Modal } from "./AdminRestaurants";

const FEATURE_LABEL = {
    voice_search: "Voice Search",
    calendar_bookings: "Calendar Bookings",
    multi_vertical: "Multi-vertical Marketplace",
    coins_loyalty: "Loyalty Coins",
    referral_program: "Referrals",
    custom_theme: "Custom Theme",
    whatsapp_notifications: "WhatsApp Notifications",
    priority_support: "Priority Support",
    api_access: "API Access",
};

export default function AdminBilling() {
    const [data, setData] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [wallet, setWallet] = useState(null);
    const [topupOpen, setTopupOpen] = useState(false);
    const [amount, setAmount] = useState("500");
    const [busy, setBusy] = useState(false);
    const [plansOpen, setPlansOpen] = useState(false);
    const [availPlans, setAvailPlans] = useState([]);
    const [cycle, setCycle] = useState("MONTHLY");

    const load = async () => {
        const [o, i, w] = await Promise.all([
            api.get("/admin/billing/overview"),
            api.get("/admin/billing/invoices"),
            api.get("/admin/wallet/platform"),
        ]);
        setData(o.data); setInvoices(i.data); setWallet(w.data);
    };
    useEffect(() => { load(); }, []);

    const topup = async () => {
        setBusy(true);
        try {
            await api.post("/admin/wallet/platform/topup", { amount: Number(amount) });
            toast.success(`+${inr2(Number(amount))} credited`);
            setTopupOpen(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const openPlans = async () => {
        try {
            const { data } = await api.get("/admin/billing/plans");
            setAvailPlans(data); setPlansOpen(true);
        } catch (e) { toast.error("Failed to load plans"); }
    };

    const changePlan = async (planId) => {
        try {
            await api.post("/admin/billing/change-plan", { plan_id: planId, billing_cycle: cycle });
            toast.success("Plan updated — payment will be billed shortly");
            setPlansOpen(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const shareInvoiceWA = async (inv) => {
        try {
            const { data } = await api.get(`/admin/billing/invoices/${inv.id}/whatsapp`);
            window.open(data.wa_url, "_blank", "noopener");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to build share link"); }
    };

    if (!data) return <p className="text-muted-foreground">Loading…</p>;
    const sub = data.subscription || {}, plan = data.plan || {}, eff = data.effective || {};
    const w = wallet?.wallet || data.wallet || {};
    const expiresIn = sub.expires_at ? Math.max(0, Math.ceil((new Date(sub.expires_at) - new Date()) / 86400000)) : 0;

    return (
        <div data-testid="admin-billing" className="space-y-6">
            <h1 className="text-2xl font-bold">Billing & Plan</h1>

            <div className="grid md:grid-cols-3 gap-4">
                {/* Current plan */}
                <div className="bg-card border border-border rounded-2xl p-5 md:col-span-2">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Current plan</p>
                    <div className="flex justify-between items-end mt-1">
                        <div>
                            <p className="text-2xl font-extrabold" data-testid="current-plan-name">{plan.name || "—"}</p>
                            <p className="text-xs text-muted-foreground">{plan.code} · billed {sub.billing_cycle?.toLowerCase()}</p>
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${sub.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : sub.status === "TRIAL" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{sub.status}</span>
                    </div>
                    <p className="text-3xl font-extrabold mt-3">{inr2(plan.monthly_price || 0)}<span className="text-xs font-medium text-muted-foreground"> /mo</span></p>
                    <p className="text-xs text-muted-foreground mt-1" data-testid="expires-in">Expires in {expiresIn} day{expiresIn === 1 ? "" : "s"} ({(sub.expires_at || "").slice(0, 10)})</p>

                    {/* Feature grid */}
                    <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-2">
                        {Object.entries(FEATURE_LABEL).map(([k, lbl]) => {
                            const on = !!eff.features?.[k];
                            return (
                                <div key={k} className={`rounded-xl px-3 py-2 text-xs font-semibold flex items-center gap-2 ${on ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                                    {on ? <Check size={13} /> : <X size={13} />}{lbl}
                                </div>
                            );
                        })}
                    </div>

                    {/* Limits */}
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(eff.limits || {}).map(([k, v]) => (
                            <div key={k} className="bg-muted/50 rounded-lg px-2 py-1.5">
                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{k.replaceAll("_", " ")}</p>
                                <p className="font-bold text-sm">{v}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Wallet */}
                <div className="bg-card border border-border rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Platform wallet</p>
                        <Wallet size={16} className="text-muted-foreground" />
                    </div>
                    <p className="text-3xl font-extrabold mt-2" data-testid="wallet-balance">{inr2(w.balance || 0)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Used to fund SMS / WhatsApp OTPs and overage charges.</p>
                    {(w.balance || 0) < (w.low_balance_threshold || 100) && (
                        <p className="text-[11px] mt-2 font-bold text-amber-700 bg-amber-100 inline-block px-2 py-1 rounded-full">⚠ Low balance</p>
                    )}
                    <button data-testid="topup-btn" onClick={() => setTopupOpen(true)} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-2.5 font-bold text-sm inline-flex items-center justify-center gap-2">
                        <ArrowUpRight size={16} /> Top-up wallet
                    </button>
                    <p className="text-[10px] text-muted-foreground text-center mt-2">Powered by Razorpay (mock)</p>
                </div>
            </div>

            {/* Invoices */}
            <section>
                <h2 className="font-bold text-lg mb-3">Invoices</h2>
                <div className="bg-card border border-border rounded-2xl overflow-x-auto">
                    <table className="w-full text-sm min-w-[640px]">
                        <thead className="bg-muted/60 text-left">
                            <tr><Th>Invoice #</Th><Th>Date</Th><Th>Total</Th><Th>Due</Th><Th>Status</Th><Th className="text-right">PDF</Th></tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => {
                                const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/billing/invoices/${inv.id}/pdf`;
                                return (
                                    <tr key={inv.id} className="border-t border-border" data-testid={`my-inv-${inv.invoice_number}`}>
                                        <Td className="font-mono text-xs">{inv.invoice_number}</Td>
                                        <Td className="text-xs">{(inv.invoice_date || "").slice(0, 10)}</Td>
                                        <Td className="font-bold">{inr2(inv.grand_total)}</Td>
                                        <Td>{inr2(inv.amount_due)}</Td>
                                        <Td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${inv.status === "PAID" ? "bg-emerald-100 text-emerald-700" : inv.status === "PARTIAL" ? "bg-amber-100 text-amber-700" : inv.status === "REFUNDED" ? "bg-purple-100 text-purple-700" : "bg-muted"}`}>{inv.status}</span></Td>
                                        <Td className="text-right">
                                            <div className="inline-flex items-center gap-2 justify-end">
                                                <button onClick={() => shareInvoiceWA(inv)} data-testid={`wa-share-${inv.invoice_number}`} className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold hover:underline">
                                                    <MessageCircle size={13} /> WhatsApp
                                                </button>
                                                <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary text-xs font-bold hover:underline">
                                                    <Download size={13} /> PDF
                                                </a>
                                            </div>
                                        </Td>
                                    </tr>
                                );
                            })}
                            {invoices.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground text-sm">No invoices yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Wallet history */}
            <section>
                <h2 className="font-bold text-lg mb-3">Wallet activity</h2>
                <div className="bg-card border border-border rounded-2xl">
                    {(wallet?.transactions || []).length === 0 ? (
                        <p className="p-6 text-center text-muted-foreground text-sm">No transactions yet.</p>
                    ) : (
                        <ul className="divide-y divide-border">
                            {(wallet?.transactions || []).slice(0, 20).map((tx) => (
                                <li key={tx.id} className="flex items-center justify-between p-3">
                                    <div>
                                        <p className="font-semibold text-sm">{tx.note || tx.type}</p>
                                        <p className="text-[11px] text-muted-foreground">{(tx.created_at || "").slice(0, 16).replace("T", " ")}</p>
                                    </div>
                                    <p className={`font-bold ${tx.amount > 0 ? "text-emerald-700" : "text-red-600"}`}>{tx.amount > 0 ? "+" : ""}{inr2(tx.amount)}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            {topupOpen && (
                <Modal title="Top-up platform wallet" onClose={() => setTopupOpen(false)}>
                    <Field label="Amount (₹)"><input data-testid="topup-amount-tenant" type="number" min="100" step="100" className="inp" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
                    <p className="text-xs text-muted-foreground mt-2">Razorpay is in mock mode — the credit is applied instantly.</p>
                    <button data-testid="topup-confirm-tenant" disabled={!amount || busy} onClick={topup} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
                        {busy && <Loader2 size={16} className="animate-spin" />} Pay ₹{amount || "0"}
                    </button>
                </Modal>
            )}

            {plansOpen && (
                <Modal title="Choose your plan" onClose={() => setPlansOpen(false)}>
                    {/* Billing-cycle pill switch */}
                    <div className="inline-flex bg-muted rounded-xl p-1 text-xs font-bold mb-4">
                        {["MONTHLY", "YEARLY"].map((c) => (
                            <button key={c} data-testid={`cycle-${c}`} onClick={() => setCycle(c)}
                                className={`px-4 py-2 rounded-lg ${cycle === c ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                                {c === "MONTHLY" ? "Monthly" : "Yearly · save ~17%"}
                            </button>
                        ))}
                    </div>

                    <div className="space-y-2">
                        {availPlans.length === 0 && <p className="text-xs text-muted-foreground">No plans available. Contact the platform admin.</p>}
                        {availPlans.map((p) => {
                            const price = cycle === "YEARLY" ? p.yearly_price : p.monthly_price;
                            const unit = cycle === "YEARLY" ? "/yr" : "/mo";
                            const isCurrent = plan.id === p.id && sub.billing_cycle === cycle;
                            return (
                                <button key={p.id} data-testid={`tenant-pick-plan-${p.code}`}
                                    disabled={isCurrent}
                                    onClick={() => changePlan(p.id)}
                                    className={`w-full text-left border rounded-xl p-3 transition ${isCurrent ? "border-emerald-500 bg-emerald-50" : "border-border hover:bg-muted/50"}`}>
                                    <div className="flex justify-between">
                                        <div>
                                            <p className="font-bold flex items-center gap-2">{p.name}{p.is_custom && <span className="text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full px-2 py-0.5">CUSTOM</span>}</p>
                                            <p className="text-[11px] text-muted-foreground mt-1">{p.limits?.max_stores} stores · {p.limits?.max_orders_per_month} orders/mo · {p.limits?.free_sms_per_month} SMS/mo · {Object.entries(p.features || {}).filter(([, v]) => v).length} features</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-extrabold text-primary text-lg">{inr2(price)}<span className="text-xs font-medium text-muted-foreground">{unit}</span></p>
                                            {isCurrent && <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Current</p>}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </Modal>
            )}
        </div>
    );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-3 text-[11px] uppercase tracking-wider font-bold text-muted-foreground ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
